// src/services/commissionsService.js
// Lectura: directo de Firestore (igual que antes, sin cambios). Escritura:
// pasa por manageComisiones.js (requiere sesión) — crear/marcar pagada o
// pendiente/borrar ya no se hacen directo contra Firestore desde el cliente.
import {
  collection, getDocs, query, orderBy, where, limit, onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { authService } from './authService';

// Ventana de las suscripciones/lecturas en vivo: `comisiones` crece sin fin
// (una por venta) y los paneles solo necesitan lo operativo. 24 meses cubre
// pendientes + histórico reciente; el análisis a más largo plazo va por
// Informes. where de rango + orderBy sobre el MISMO campo → sin índice compuesto.
function ventanaComisiones() {
  const d = new Date();
  d.setMonth(d.getMonth() - 24);
  return d.toISOString().slice(0, 10); // 'YYYY-MM-DD' (mismo formato que fechaCreacion)
}
const LIMITE_COMISIONES = 4000;

async function request(url, opts = {}) {
  const resp = await fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authService.authHeader(), ...(opts.headers || {}) },
  });
  const data = await resp.json();
  if (!data.success) throw new Error(data.error || 'Error de red');
  return data;
}

// Obtener todas las comisiones (one-shot)
export const getCommissions = async () => {
  const q = query(
    collection(db, 'comisiones'),
    where('fechaCreacion', '>=', ventanaComisiones()),
    orderBy('fechaCreacion', 'desc'),
    limit(LIMITE_COMISIONES),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Suscripción en tiempo real — devuelve la función de cancelación
export const subscribeToCommissions = (onData, onError) => {
  const q = query(
    collection(db, 'comisiones'),
    where('fechaCreacion', '>=', ventanaComisiones()),
    orderBy('fechaCreacion', 'desc'),
    limit(LIMITE_COMISIONES),
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (err) => { console.error('subscribeToCommissions:', err); onError?.(err); }
  );
};

// Crear comisión — cualquier sesión válida (el técnico se autopropone,
// siempre queda en PENDIENTE hasta que un admin la marca pagada)
export const createCommission = async (data) => {
  const result = await request('/.netlify/functions/manageComisiones', {
    method: 'POST', body: JSON.stringify(data),
  });
  return { id: result.id, ...data, estado: 'PENDIENTE', fechaPago: null };
};

// Marcar como pagada (solo admin/coordinador)
export const markCommissionAsPaid = async (id) => {
  await request('/.netlify/functions/manageComisiones', {
    method: 'PATCH', body: JSON.stringify({ id, estado: 'PAGADA' }),
  });
};

// Marcar como pendiente (solo admin/coordinador)
export const markCommissionAsPending = async (id) => {
  await request('/.netlify/functions/manageComisiones', {
    method: 'PATCH', body: JSON.stringify({ id, estado: 'PENDIENTE' }),
  });
};

// Eliminar comisión (solo admin/coordinador)
export const deleteCommission = async (id) => {
  await request(`/.netlify/functions/manageComisiones?id=${id}`, { method: 'DELETE' });
};

// Obtener por empleado (útil para vistas individuales)
export const getCommissionsByEmployee = async (employeeId) => {
  const q = query(
    collection(db, 'comisiones'),
    where('employeeId', '==', employeeId),
    orderBy('fechaCreacion', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
